import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Star, ChevronLeft, ChevronRight, Quote, Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { API } from '@/utils/constants';

const StarRating = ({ value, onChange, size = 24, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            type="button"
            key={n}
            onClick={() => !readOnly && onChange?.(n)}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            className={`${readOnly ? 'cursor-default' : 'cursor-pointer'} transition-transform ${!readOnly && 'hover:scale-110'}`}
            aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
            data-testid={`star-${n}${readOnly ? '-readonly' : ''}`}
          >
            <Star
              size={size}
              className={filled ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}
            />
          </button>
        );
      })}
    </div>
  );
};

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', rating: 0, title: '', comment: '' });

  const loadReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data.reviews || []);
      setAverage(res.data.average_rating || 0);
      setTotal(res.data.total_count || 0);
    } catch (err) {
      // Silent fail; section just won't show carousel
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  // Inject JSON-LD structured data for SEO (rich results with stars in Google)
  useEffect(() => {
    if (reviews.length === 0 || total === 0) return;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Julien Jaccard - Rééducation respiratoire",
      "description": "Physiothérapeute spécialisé dans la rééducation des troubles respiratoires : hyperventilation, respiration dysfonctionnelle, asthme, COVID long.",
      "url": typeof window !== 'undefined' ? window.location.origin : '',
      "image": typeof window !== 'undefined' ? `${window.location.origin}/enso-new.png` : undefined,
      "priceRange": "$$",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": average.toFixed(1),
        "reviewCount": total,
        "bestRating": "5",
        "worstRating": "1",
      },
      "review": reviews.slice(0, 20).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.name },
        "datePublished": new Date(r.created_at).toISOString().split('T')[0],
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": r.rating,
          "bestRating": "5",
          "worstRating": "1",
        },
        "name": r.title,
        "reviewBody": r.comment,
      })),
    };

    const scriptId = 'reviews-jsonld-schema';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, [reviews, average, total]);

  // Auto-rotate every 7s
  useEffect(() => {
    if (reviews.length <= 1) return;
    const t = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % reviews.length);
    }, 7000);
    return () => clearInterval(t);
  }, [reviews.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.title.trim() || !form.comment.trim() || form.rating < 1) {
      toast.error('Merci de remplir tous les champs et de noter votre expérience.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: form.name.trim(),
        rating: form.rating,
        title: form.title.trim(),
        comment: form.comment.trim(),
      });
      setSubmitted(true);
      setForm({ name: '', rating: 0, title: '', comment: '' });
      toast.success('Merci ! Votre avis sera publié après modération.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'envoi de votre avis.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de l\'envoi.');
    } finally {
      setSubmitting(false);
    }
  };

  const prev = () => setCurrentIndex((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setCurrentIndex((i) => (i + 1) % reviews.length);

  const current = reviews[currentIndex];

  return (
    <section className="relative z-10 py-20 bg-gradient-to-b from-white/40 to-cyan-50/40" data-testid="reviews-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-3"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Témoignages
          </h2>
          <p className="text-cyan-100/80 max-w-2xl mx-auto">
            Découvrez les retours des personnes ayant utilisé ces ressources.
          </p>

          {total > 0 && (
            <div className="mt-6 inline-flex flex-col items-center gap-2 px-6 py-4 rounded-2xl bg-white/80 backdrop-blur shadow-sm border border-stone-200" data-testid="reviews-summary">
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold text-slate-800">{average.toFixed(1)}</span>
                <StarRating value={Math.round(average)} readOnly size={22} />
              </div>
              <span className="text-sm text-slate-500">
                Basé sur {total} avis
              </span>
            </div>
          )}
        </div>

        {/* Carousel */}
        {!loading && reviews.length > 0 && (
          <div className="relative mb-12" data-testid="reviews-carousel">
            <Card className="border-0 shadow-lg bg-white/90 backdrop-blur">
              <CardContent className="p-8 md:p-12 min-h-[260px]">
                <Quote className="text-cyan-500/30 mb-4" size={40} />
                <div className="space-y-4">
                  <StarRating value={current.rating} readOnly size={20} />
                  <h3 className="text-xl font-bold text-slate-800" data-testid="review-title">
                    {current.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed italic" data-testid="review-comment">
                    &ldquo;{current.comment}&rdquo;
                  </p>
                  <div className="pt-2 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700" data-testid="review-author">
                      — {current.name}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(current.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {reviews.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 md:-translate-x-5 w-10 h-10 rounded-full bg-white shadow-md hover:bg-cyan-50 flex items-center justify-center transition"
                  aria-label="Avis précédent"
                  data-testid="reviews-prev-btn"
                >
                  <ChevronLeft size={20} className="text-slate-700" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 md:translate-x-5 w-10 h-10 rounded-full bg-white shadow-md hover:bg-cyan-50 flex items-center justify-center transition"
                  aria-label="Avis suivant"
                  data-testid="reviews-next-btn"
                >
                  <ChevronRight size={20} className="text-slate-700" />
                </button>

                {/* Dots */}
                <div className="flex justify-center gap-2 mt-4">
                  {reviews.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2 rounded-full transition-all ${idx === currentIndex ? 'w-6 bg-cyan-500' : 'w-2 bg-stone-300'}`}
                      aria-label={`Aller à l'avis ${idx + 1}`}
                      data-testid={`reviews-dot-${idx}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Submission Form */}
        <Card className="border-0 shadow-lg bg-white/95 backdrop-blur" data-testid="review-form-card">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Laissez votre avis
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              Votre avis sera publié après vérification par l&apos;administrateur.
            </p>

            {submitted ? (
              <div className="flex flex-col items-center text-center py-8" data-testid="review-thank-you">
                <CheckCircle className="text-emerald-500 mb-3" size={48} />
                <p className="text-lg font-semibold text-slate-800">Merci pour votre retour !</p>
                <p className="text-sm text-slate-500 mt-1">Votre avis sera publié après modération.</p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => setSubmitted(false)}
                  data-testid="review-add-another-btn"
                >
                  Laisser un autre avis
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="review-form">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre nom <span className="text-rose-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={80}
                    placeholder="Ex: Marie D."
                    required
                    data-testid="review-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre note <span className="text-rose-500">*</span>
                  </label>
                  <StarRating
                    value={form.rating}
                    onChange={(r) => setForm({ ...form, rating: r })}
                    size={32}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Titre <span className="text-rose-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    maxLength={120}
                    placeholder="Ex: Une approche qui change la vie"
                    required
                    data-testid="review-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Votre commentaire <span className="text-rose-500">*</span>
                  </label>
                  <Textarea
                    value={form.comment}
                    onChange={(e) => setForm({ ...form, comment: e.target.value })}
                    maxLength={2000}
                    rows={5}
                    placeholder="Partagez votre expérience..."
                    required
                    data-testid="review-comment-input"
                  />
                  <p className="text-xs text-slate-400 mt-1 text-right">
                    {form.comment.length}/2000
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-8 py-6 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
                  data-testid="review-submit-btn"
                >
                  {submitting ? 'Envoi...' : (
                    <>
                      <Send className="mr-2" size={18} />
                      Envoyer mon avis
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ReviewsSection;
